import { PropsWithChildren, ReactNode } from 'react';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

export enum StatusType {
  OK = 'ok',
  WARNING = 'warning',
  ERROR = 'error',
  PENDING = 'pending',
  RUNNING = 'running',
  ABORTED = 'aborted',
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '100%',
    marginRight: 8,
    flexShrink: 0,
  },
  ok: {
    backgroundColor: theme.palette.success.main,
  },
  warning: {
    backgroundColor: theme.palette.warning.main,
  },
  error: {
    backgroundColor: theme.palette.error.main,
  },
  pending: {
    backgroundColor: theme.palette.info.main,
  },
  running: {
    backgroundColor: theme.palette.info.main,
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
  aborted: {
    backgroundColor: theme.palette.grey[500],
  },
}));

interface StatusProps {
  type: StatusType;
  children: ReactNode;
}

export const StateStatus = ({ type, children }: StatusProps) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classNames(classes.dot, classes[type])} />
      {children}
    </div>
  );
};

// For backward compatibility, export the individual status components
export const StatusOK = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.OK}>{children}</StateStatus>
);

export const StatusWarning = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.WARNING}>{children}</StateStatus>
);

export const StatusError = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.ERROR}>{children}</StateStatus>
);

export const StatusPending = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.PENDING}>{children}</StateStatus>
);

export const StatusRunning = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.RUNNING}>{children}</StateStatus>
);

export const StatusAborted = ({ children }: PropsWithChildren) => (
  <StateStatus type={StatusType.ABORTED}>{children}</StateStatus>
);
